import { useContext, VFC } from "react";
// Libraries
import { sortBy } from "lodash";
import { useQuery } from "@apollo/client";
import { NavLink } from "react-router-dom";
// API
import { GetBusinessUnits } from "api/BusinessUnits/types/GetBusinessUnits";
// State
import { Species } from "state";
// Utils
import { GET_BUSINESS_UNITS_LIST } from "api/BusinessUnits/queries";
import { AppContext } from "config/appProvider";
import { BUSINESS_SETTINGS, SETTINGS } from "constants/Routes";
import { BU_TYPES } from "trading/constants";
// Hooks
import { useGetBusinessUnitUserRole, useHasFeature } from "hooks";
// Containers
import { DashboardBusinessUnitSelect, DashboardSpeciesSelect, DashboardUser } from "containers/dashboard";
// Components
import { LoadingOverlay, Flex, SnackBarNotification } from "components";
// Resources
import styles from "./DashboardHeader.module.scss";

export const DashboardHeader: VFC = () => {
  const { mainAppState } = useContext(AppContext);
  const { isAdmin } = useGetBusinessUnitUserRole();

  const currentBusinessUnitID = mainAppState.selectedBU.toString();
  const isProcessor = mainAppState.selectedBUType === BU_TYPES.processor;
  const isShowNagBCMSMessage: boolean = useHasFeature("SHOW_NAG_MESSAGE_ABOUT_BCMS");
  const hasSpeciesSwitchFeature: boolean = useHasFeature("SPECIES_SWITCH");

  const { data, loading: isLoadingGetBusinessUnits } = useQuery<GetBusinessUnits>(GET_BUSINESS_UNITS_LIST, {
    fetchPolicy: "network-only",
  });
  const businessUnits = data?.businessUnits || [];
  const currentBusinessUnit = businessUnits.find((item) => item?.id === currentBusinessUnitID);

  const isBusinessUnitedIntegratedWithBCMS = !currentBusinessUnit?.warnings.IS_NOT_BCMS_INTEGRATED;

  const currentBusinessUnitSpecies = sortBy(currentBusinessUnit?.farmAnimalTypes, "position")
    .filter(({ enabled }) => enabled)
    .map(
      (animalType, index): Species => ({
        ...animalType,
        active: index === 0,
      }),
    );
  const hasSpecies = currentBusinessUnitSpecies.length > 0;

  return (
    <>
      {!isLoadingGetBusinessUnits && !isBusinessUnitedIntegratedWithBCMS && !isProcessor && isShowNagBCMSMessage && (
        <SnackBarNotification
          open={true}
          variant={"error"}
          message={
            isAdmin ? (
              <span>
                This farm is not connected to a regulatory system. To complete setup, go to{" "}
                <NavLink className={styles.link} to={`${SETTINGS}${BUSINESS_SETTINGS}`}>
                  Farm Settings
                </NavLink>
              </span>
            ) : (
              `This farm is not connected to a regulatory system. ` +
              `Please see your farm's administrator to complete the setup.`
            )
          }
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        />
      )}

      <Flex className={styles.dashboard_header} container containerJustifyContent="flex-end">
        <Flex
          className={styles.dashboard_header__inner}
          container
          containerWrap="nowrap"
          item
          xs={12}
          m={6}
          l={4}
          xl={3}
        >
          <Flex className={styles.dashboard_header__business_unit_select} item itemGrow itemGutter>
            {isLoadingGetBusinessUnits ? (
              <LoadingOverlay customStyles={styles.dashboard_header__business_unit_select_loading} />
            ) : (
              <DashboardBusinessUnitSelect businessUnits={businessUnits} />
            )}
          </Flex>

          {hasSpeciesSwitchFeature && hasSpecies && (
            <Flex className={styles.dashboard_header__species_select} item>
              {isLoadingGetBusinessUnits ? (
                <LoadingOverlay customStyles={styles.dashboard_header__species_select_loading} />
              ) : (
                <DashboardSpeciesSelect species={currentBusinessUnitSpecies} />
              )}
            </Flex>
          )}

          <Flex item itemGutter itemNoShrink>
            <DashboardUser />
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};
